<template>
  <div
    class="d-flex stats__element"
    :class="{'stats__element__custom': isReady && stat && stat.type.includes('Stat')}"
  >
    <div
      v-if="isReady"
      class="stats__element__wrapper"
      :class="{disabled: disabled, 'stats__element__wrapper__custom': stat.type.includes('Stat')}"
    >
      <div
        v-if="stat.type === 'userStat'"
        class="custom-stat user-stat"
      >
        <div class="ml-1 stat-meta">
          <icon :icon="stat.meta.gdpr ? 'gdpr' : 'non-gdpr'" />
          <span class="ml-1">
            {{ !stat.meta.gdpr ? 'Not' : '' }} GDPR Compliant
          </span>
        </div>

        <hr>

        <div class="ml-1 stat-meta">
          <icon :icon="readerIcon" />
          <span class="ml-1">
            {{ readerLabel }}
          </span>
        </div>
      </div>

      <div
        v-if="stat.type === 'accountStat'"
        class="custom-stat account-stat"
      >
        <div class="ml-1 stat-meta">
          <icon
            :icon="stat.meta.mifid ? 'tick-badge' : 'cross-badge'"
            :class="{negative: !stat.meta.mifid, positive: stat.meta.mifid }"
          />
          <span class="ml-1">
            {{ !stat.meta.mifid ? 'Not' : '' }} MIFID II Compliant
          </span>
        </div>

        <hr>

        <div class="ml-1 stat-meta">
          <span>{{ stat.meta.account_type }}</span>
        </div>
      </div>

      <div v-if="!stat.type.includes('Stat')">
        <div class="d-flex justify-content-center float-left align-items-center stats__element__icon">
          <icon :icon="iconName" />
        </div>
        <div class="d-flex flex-column stats__element__data">
          <div class="stats__element__data__value">
            <span>{{ prettyValue }}{{ ['scrollDepth', 'sendgridOpenRate'].includes(stat.type) ? '%' : '' }}</span>

            <popover
              v-if="stat.type === 'engMinsArticlesRatio'"
              :offset="0"
              :open-group="stat.type"
            >
              Impact ratio calculated as:

              <div class="mt-1 frac-wrapper">
                <div class="ml-1 frac">
                  <span class="top">
                    Impact Score ({{ stats.engagedMinutes.value }})
                  </span>
                  <span class="bottom">
                    New Articles ({{ stats.newArticles.value }})
                  </span>
                </div>
              </div>
            </popover>

            <popover
              v-if="popover"
              :offset="0"
              :open-group="stat.type"
            >
              {{ popover }}
            </popover>
          </div>
          <div class="stats__element__data__label">
            {{ label }}
          </div>
          <div
            v-if="stat.change"
            class="stats__element__data__change"
            :class="percentClass"
          >
            <icon :icon="chevronType" />
            {{ Math.abs(stat.change) }}%
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="loading-wrapper w-100"
    >
      <loading-icon
        :loading="!isReady"
        loading-class="text-center mx-auto"
        style="display:flex;width:100%;height:100%;justify-items: center;"
      />
    </div>
  </div>
</template>

<script>

const types = {
  emailOpens: {
    icon: 'reader',
    label: 'Total Email Opens',
    popover: 'A subset of email opens, this number represents the number of times an email was viewed for eight seconds or longer.',
  },
  sendgridOpenedUnique: {
    icon: 'reader',
    label: 'Total Email Opens',
    popover: 'A subset of email opens, this number represents the number of times an email was viewed for eight seconds or longer.',

  },
  emailNewsletterViews: {
    icon: 'email-view',
    label: 'Total Email Views',
    popover: 'More accurate than opens, email views count the number of times people actually saw an email (not a spam filter or mail server).',
  },
  webViews: {
    // icon: 'web-view-stat',
    icon: 'web-view-stat',
    label: 'Total Web Views',
    popover: 'The number of article views on the insights website.',
  },
  sendgridProcessed: {
    icon: 'consumer',
    label: 'Total Recipients',
  },
  sendgridProcessedUnique: {
    icon: 'consumer',
    label: 'Recipients',
  },
  emailDelivers: {
    icon: 'paper-airplane',
    label: 'Delivered',
  },
  sendgridOpenRate: {
    icon: 'email-view',
    label: 'Open Rate',
  },
  emailClicks: {
    icon: 'mouse',
    label: 'Click Throughs',
  },
  sendgridClickedUnique: {
    icon: 'mouse',
    label: 'Unique Click Throughs',
  },
  newArticles: {
    icon: 'article',
    label: 'New Articles',
  },
  engagedMinutes: {
    icon: 'cubelogo',
    label: 'Impact Score',
    popover: 'An index score based on engagement with articles on the web.',
  },
  engMinsArticlesRatio: {
    icon: 'article-engagement',
    label: 'Impact Ratio',
  },
  engagedMinutesNewsletter: {
    icon: 'cubelogo',
    label: 'Impact Score',
  },
  webDownloads: {
    icon: 'download',
    label: 'Unique Downloads',
    popover: 'The number of different users that have downloaded the PDF of the article.',
  },
  scrollDepth: {
    icon: 'mouse',
    label: 'Scroll Depth',
  },
}

export default {
  components: {
  },
  props: {
    stats: {
      type: Object,
      default: null,
    },
    prop: {
      type: String,
      required: true,
    },
    props: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      required: true,
    },
  },
  computed: {
    iconName () {
      return types[this.stat.type].icon
    },
    label () {
      return types[this.stat.type].label
    },
    popover () {
      return types[this.stat.type].popover
    },
    percentClass () {
      return this.stat.change < 0 ? 'negative' : 'positive'
    },
    chevronType () {
      return this.stat.change < 0 ? 'chevron-down' : 'chevron-up'
    },
    readerIcon () {
      const read = this.stat.meta.reader

      if (isNaN(read)) {
        return 'non-reader'
      } else if (read < 4) {
        return 'glancer'
      } else if (read < 8) {
        return 'skimmer'
      } else {
        return 'reader'
      }
    },
    readerLabel () {
      const read = this.stat.meta.reader

      if (isNaN(read)) {
        return 'No Reader'
      } else if (read <= 4) {
        return 'Glance Reader'
      } else if (read <= 8) {
        return 'Skim Reader'
      } else {
        return 'Full Reader'
      }
    },
    isReady () {
      console.log('ready?',this.stat)
      return this.stat !== null
    },
    stat () {
      return this.stats != null && this.prop in this.stats ? this.stats[this.prop] : null
    },
    value () {
      return this.stat.type === 'engMinsArticlesRatio' && this.stat.value <= 0 ? '-' : this.stat.value
    },
    prettyValue () {
      return this.value.toLocaleString()
    },
  },
}
</script>

<style lang="scss">
  .frac {
    display: inline-block;
    position: relative;
    vertical-align: middle;
    letter-spacing: 0.001em;
    text-align: center;
  }
  .frac > span {
    display: block;
    padding: 0.1em;
  }
  .frac span.bottom {
    border-top: thin solid white;
  }

</style>
